import {ArrowRightIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Dialog, FormControl, Radio, RadioGroup, TextInput} from '@primer/react'
import {useCallback, useState} from 'react'
import {useGetStartNewCommentOnLine} from '../../stores/selectors'
import {FileLevelCode} from '../../stores/slices/move-new-comment'
import type {CommentLineChangeConfig} from './AddCommentEditor'
import type {FileKey, LineKey} from '../../types'
import type {DiffSide} from '../../conversation-types'
import type {DiffLineKey} from '../../stores/slices/helpers/diff-lines-slice-helpers'

export type LineNumberOption = 'line' | 'lineRange' | 'file'

export function ChangeCommentLineNumberButton({
  currentLineNumberText,
  setCommentLineChangeConfig,
  baseEndLineNumber,
  diffSide,
  baseStartLineNumber,
}: {
  currentLineNumberText: string
  setCommentLineChangeConfig: (config: CommentLineChangeConfig) => void
  diffSide: DiffSide
  baseEndLineNumber?: number
  baseStartLineNumber?: number
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<LineNumberOption>(() => {
    if (baseStartLineNumber && baseEndLineNumber) {
      return 'lineRange'
    }
    if (baseEndLineNumber) {
      return 'line'
    }
    return 'file'
  })
  const [isLeftSelected, setIsLeftSelected] = useState(diffSide === 'LEFT')
  const [startLineNumber, setStartLineNumber] = useState(baseStartLineNumber ? String(baseStartLineNumber) : '')
  const [endLineNumber, setEndLineNumber] = useState(baseEndLineNumber ? String(baseEndLineNumber) : '')
  const [errorText, setErrorText] = useState<string>('')
  const getStartNewCommentOnLine = useGetStartNewCommentOnLine()

  const submit = useCallback(() => {
    const endLineData = getStartNewCommentOnLine(
      getLineKey(isLeftSelected, Number(endLineNumber), selectedOption === 'file'),
    )
    const startLineData = getStartNewCommentOnLine(
      getLineKey(isLeftSelected, Number(startLineNumber), selectedOption === 'file'),
    )
    const startConvoOnNewLine = endLineData?.startNewComment
    const endDiffLineKey = endLineData?.diffLineKey
    const startDiffLineKey = startLineData?.diffLineKey
    if (
      !validateLineNumberInput(selectedOption === 'lineRange' ? startLineNumber : undefined, endLineNumber) &&
      selectedOption !== 'file'
    ) {
      setErrorText('Please enter a valid line number or range.')
    } else {
      if (startConvoOnNewLine && endDiffLineKey) {
        // there is a valid line to start a new comment on because the line key had data associated with it
        const commentLineChangeConfig = buildCommentLineChangeConfig({
          isLeftSide: isLeftSelected,
          startLineNumber: selectedOption === 'lineRange' ? Number(startLineNumber) : undefined,
          endDiffLineKey,
          endLineNumber: Number(endLineNumber),
          isFileLevelComment: selectedOption === 'file',
          startDiffLineKey: selectedOption === 'lineRange' ? startDiffLineKey : undefined,
          startConversationOnNewLine: startConvoOnNewLine,
          closeLineMovingDialog: () => setIsDialogOpen(false),
          setErrorMessage: message => setErrorText(message),
        })
        setCommentLineChangeConfig(commentLineChangeConfig)
      } else {
        setErrorText('Please enter a valid line number or range.')
      }
    }
  }, [
    getStartNewCommentOnLine,
    isLeftSelected,
    endLineNumber,
    selectedOption,
    startLineNumber,
    setCommentLineChangeConfig,
  ])

  return (
    <div className="ml-1">
      <Button type="button" size="small" onClick={() => setIsDialogOpen(true)} trailingAction={TriangleDownIcon}>
        {currentLineNumberText}
      </Button>
      {isDialogOpen && (
        <Dialog
          title="Add comment on"
          width="medium"
          onClose={() => setIsDialogOpen(false)}
          footerButtons={[
            {
              buttonType: 'default',
              content: 'Cancel',
              onClick: () => {
                setIsDialogOpen(false)
              },
            },
            {
              buttonType: 'primary',
              content: 'Save',
              onClick: submit,
            },
          ]}
        >
          <RadioGroup
            name="line-number-selection"
            onChange={value => {
              setSelectedOption(value as LineNumberOption)
            }}
          >
            <RadioGroup.Label />
            <FormControl>
              <Radio value="line" defaultChecked={selectedOption === 'line'} />
              <FormControl.Label>Line number</FormControl.Label>
              {selectedOption === 'line' && (
                <FormControl.Caption className="d-flex align-items-center">
                  <MoveCommentLineNumberDropdown
                    isLeftSelected={isLeftSelected}
                    setIsLeftSelected={setIsLeftSelected}
                  />
                  <TextInput
                    autoComplete="off"
                    className="ml-1"
                    aria-label="Line number"
                    name="lineNumber"
                    value={endLineNumber}
                    onChange={e => setEndLineNumber(e.target.value)}
                    onKeyDown={e => {
                      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                      if (e.key === 'Enter') {
                        submit()
                      }
                    }}
                  />
                </FormControl.Caption>
              )}
            </FormControl>
            <FormControl>
              <Radio value="lineRange" defaultChecked={selectedOption === 'lineRange'} />
              <FormControl.Label>Line range</FormControl.Label>
              {selectedOption === 'lineRange' && (
                <FormControl.Caption className="d-flex align-items-center">
                  <MoveCommentLineNumberDropdown
                    isLeftSelected={isLeftSelected}
                    setIsLeftSelected={setIsLeftSelected}
                  />
                  <TextInput
                    autoComplete="off"
                    className={'ml-1'}
                    aria-label="Line number"
                    name="lineNumber"
                    value={startLineNumber}
                    onChange={e => setStartLineNumber(e.target.value)}
                    onKeyDown={e => {
                      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                      if (e.key === 'Enter') {
                        submit()
                      }
                    }}
                  />
                  <ArrowRightIcon className="mt-2 mx-2" />
                  <TextInput
                    autoComplete="off"
                    className="ml-1"
                    aria-label="Line number"
                    name="lineNumber"
                    value={endLineNumber}
                    onChange={e => setEndLineNumber(e.target.value)}
                    onKeyDown={e => {
                      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                      if (e.key === 'Enter') {
                        submit()
                      }
                    }}
                  />
                </FormControl.Caption>
              )}
            </FormControl>
            <FormControl>
              <Radio value="file" defaultChecked={selectedOption === 'file'} />
              <FormControl.Label>File</FormControl.Label>
            </FormControl>
            {errorText && <RadioGroup.Validation variant="error">{errorText}</RadioGroup.Validation>}
          </RadioGroup>
        </Dialog>
      )}
    </div>
  )
}

const leftText = 'Left (L)'
const rightText = 'Right (R)'

function MoveCommentLineNumberDropdown({
  isLeftSelected,
  setIsLeftSelected,
}: {
  isLeftSelected: boolean
  setIsLeftSelected: (value: boolean) => void
}) {
  const textToUse = isLeftSelected ? leftText : rightText
  return (
    <ActionMenu>
      <ActionMenu.Button>{textToUse}</ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item
            onSelect={() => {
              setIsLeftSelected(true)
            }}
          >
            {leftText}
          </ActionList.Item>
          <ActionList.Item
            onSelect={() => {
              setIsLeftSelected(false)
            }}
          >
            {rightText}
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function getLineKey(isLeftSide: boolean, lineNumber: number, isFile: boolean): LineKey | FileKey {
  if (isFile) {
    return FileLevelCode
  }
  const sideIdentifier = isLeftSide ? 'L' : 'R'
  return `${sideIdentifier}${lineNumber}`
}

function buildCommentLineChangeConfig(args: {
  isLeftSide: boolean
  startLineNumber: number | undefined
  endDiffLineKey: DiffLineKey
  endLineNumber: number
  isFileLevelComment: boolean
  setErrorMessage: (message: string) => void
  closeLineMovingDialog: () => void
  startDiffLineKey: DiffLineKey | undefined
  startConversationOnNewLine: () => void
}): CommentLineChangeConfig {
  return {
    diffSide: args.isLeftSide ? 'LEFT' : 'RIGHT',
    startLineNumber: args.startLineNumber,
    endLineNumber: args.endLineNumber,
    isFileLevelComment: args.isFileLevelComment,
    startDiffLineKey: args.startDiffLineKey,
    endDiffLineKey: args.endDiffLineKey,
    setErrorMessage: args.setErrorMessage,
    startConversationOnNewLine: args.startConversationOnNewLine,
    closeLineMovingDialog: args.closeLineMovingDialog,
  }
}

function validateLineNumberInput(startLine: string | undefined, endLine: string | undefined): boolean {
  const startLineNumber = Number(startLine)
  const endLineNumber = Number(endLine)
  return (
    Number.isInteger(endLineNumber) &&
    endLineNumber > 0 &&
    (startLine === undefined ||
      (Number.isInteger(startLineNumber) && startLineNumber > 0 && startLineNumber <= endLineNumber))
  )
}

try{ ChangeCommentLineNumberButton.displayName ||= 'ChangeCommentLineNumberButton' } catch {}
try{ MoveCommentLineNumberDropdown.displayName ||= 'MoveCommentLineNumberDropdown' } catch {}